'use client'

import { useCallback, useEffect, useRef } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import type { Route } from 'next'

let inAppNavCount = 0

/**
 * router.back() is wrong when the error page is the first history entry —
 * a deep link, a PWA cold start, or a page refresh — because it exits the
 * app entirely. This tracks in-app navigations at module scope and falls
 * back to a safe replace() when there's nowhere real to go back to.
 */
export function useSafeBack(fallbackHref: string) {
  const router = useRouter()
  const pathname = usePathname()
  const lastPathname = useRef(pathname)

  useEffect(() => {
    if (lastPathname.current !== pathname) {
      inAppNavCount += 1
      lastPathname.current = pathname
    }
  }, [pathname])

  return useCallback(() => {
    if (inAppNavCount > 0 && typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
    } else {
      router.replace(fallbackHref as Route)
    }
  }, [router, fallbackHref])
}
